import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useNotification } from './NotificationContext';
import { useAuth } from './AuthContext';

// Create the context
export const EmployeeContext = createContext();

// Custom hook for easier usage
export const useEmployee = () => {
  const context = useContext(EmployeeContext);
  if (!context) {
    throw new Error('useEmployee must be used within an EmployeeProvider');
  }
  return context;
};

export const EmployeeProvider = ({ children }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const { success, error: showError } = useNotification();
  const { isAuthenticated, user } = useAuth();

  // ✅ Get employees
  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      console.log('👥 EmployeeContext: Fetching employees...');
      const response = await api.get('/employee/all'); // API endpoint for fetching employees
      console.log('👥 EmployeeContext: Employees response:', response);
      setEmployees(response?.data?.data || []);
    } catch (error) {
      console.error('👥 EmployeeContext: Error fetching employees:', error);
      // Only show error if user is authenticated (don't show errors on login screen)
      if (isAuthenticated) {
        showError('Failed to load employees');
      }
    } finally {
      setLoading(false);
    }
  }, [showError, isAuthenticated]);

  // ✅ Clear employees when logged out, or fetch when logged in
  useEffect(() => {
    if (!isAuthenticated) {
      setEmployees((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    if (user?.role === 'admin' || user?.role === 'manager' || user?.role === 'supervisor') {
      fetchEmployees();
    }
  }, [isAuthenticated, user?.role, fetchEmployees]);

  // ✅ Create new employee
  const createEmployee = useCallback(async (employeeData) => {
    try {
      setLoading(true);
      const response = await api.post('/employee', employeeData);
      fetchEmployees()
      success('Employee created successfully');
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create employee';
      showError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchEmployees, success, showError]);

  // ✅ Update employee
  const updateEmployee = useCallback(async (id, updatedData) => {
    try {
      setLoading(true);
      const response = await api.put(`/employee/${id}`, updatedData);
      if (response.status >= 200 && response.status < 300) {
        fetchEmployees();
        success('Employee updated successfully');
        return { success: true, data: response.data };
      }
      return { success: true, data: response?.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update employee';
      showError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [fetchEmployees, success, showError]);

  // ✅ Delete employee
  const deleteEmployee = useCallback(async (id) => {
    try {
      setLoading(true);
      await api.delete(`/employee/${id}`);
      setEmployees((prev) => prev.filter((emp) => emp._id !== id));
      success('Employee deleted successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete employee';
      showError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, [success, showError]);

  const value = {
    employees,
    loading,
    fetchEmployees,
    createEmployee,
    updateEmployee,
    deleteEmployee,
  };

  return (
    <EmployeeContext.Provider value={value}>
      {children}
    </EmployeeContext.Provider>
  );
};
